import { Box, CircularProgress } from '@mui/material';
import { Typography } from '@peritasai/ui-components';
import { useTranslation } from 'react-i18next';
import { VERIFY_ASSETS_SUCCESS_COLOR } from '../colors';
import { DetectedCheckIcon } from './DetectedCheckIcon';

export type AssetItemState = 'loading' | 'detected' | 'confirmed';

export interface AssetItemRowProps {
  name: string;
  state: AssetItemState;
  assetId?: string;
}

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  height: '57px',
  borderRadius: '8px',
  borderWidth: '1px',
  borderStyle: 'solid',
} as const;

const stateSx: Record<AssetItemState, object> = {
  loading: {
    ...rowSx,
    backgroundColor: 'background.paper',
    borderColor: 'divider',
  },
  detected: {
    ...rowSx,
    backgroundColor: 'background.paper',
    borderColor: 'divider',
  },
  confirmed: {
    ...rowSx,
    backgroundColor: 'background.paper',
    borderColor: 'divider',
  },
};

const nameColorByState: Record<AssetItemState, string> = {
  loading: 'text.primary',
  detected: 'text.primary',
  confirmed: 'text.primary',
};

export function AssetItemRow({ name, state, assetId }: AssetItemRowProps) {
  const { t } = useTranslation(['periverse']);

  return (
    <Box sx={stateSx[state]} data-testid="asset-item-row" data-state={state}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: nameColorByState[state], fontWeight: 700 }}
          noWrap
        >
          {name}
        </Typography>
        {state !== 'loading' && assetId && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: 1.43,
              letterSpacing: '0.17px',
            }}
          >
            {state === 'confirmed'
              ? t('pages.caseVerifyAssets.itemStatus.confirmed', { assetId })
              : t('pages.caseVerifyAssets.itemStatus.detected', { assetId })}
          </Typography>
        )}
      </Box>

      {state === 'loading' && (
        <CircularProgress
          size={32}
          color="primary"
          aria-label={t('pages.caseVerifyAssets.itemStatus.loadingLabel', {
            name,
          })}
        />
      )}

      {(state === 'detected' || state === 'confirmed') && (
        <DetectedCheckIcon
          data-testid="asset-item-row-check"
          sx={{
            fontSize: 35,
            color:
              state === 'detected'
                ? VERIFY_ASSETS_SUCCESS_COLOR
                : 'action.active',
          }}
        />
      )}
    </Box>
  );
}
